import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, User, Ban, Trash2, Crown, Check, X, 
  Mail, Calendar, MapPin, Heart, Shield, Filter,
  ChevronDown, ChevronUp, RefreshCw, UserCheck, AlertTriangle, Coins
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import AdminLayout from "@/components/AdminLayout";

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");

  // Debounce search input - only reset page when search actually changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1); // Only reset to page 1 when search term changes
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const fetchUsers = useCallback(async (currentPage, currentSearch) => {
    setLoading(true);
    try {
      console.log(`[AdminUsers] Fetching page ${currentPage} with search: "${currentSearch}"`);
      const response = await api.getAdminUsers({ page: currentPage, limit: 20, search: currentSearch || "" });
      console.log(`[AdminUsers] Got ${response.data.users?.length} users, total: ${response.data.total}, pages: ${response.data.pages}`);
      
      const newUsers = response.data.users || [];
      const newTotalPages = response.data.pages || 1;
      const newTotalUsers = response.data.total || 0;
      
      // Always update with received data
      setUsers(newUsers);
      setTotalPages(newTotalPages);
      setTotalUsers(newTotalUsers);
      
    } catch (error) {
      console.error("Failed to load users:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error("Failed to load users: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch users when page or search changes
  useEffect(() => {
    fetchUsers(page, search);
  }, [page, search, fetchUsers]);

  const handleSuspend = async (userId, isSuspended) => {
    try {
      await api.updateAdminUser(userId, { is_suspended: !isSuspended });
      toast.success(isSuspended ? "User unsuspended" : "User suspended");
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleVerify = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/verify-email`);
      toast.success("Email verified");
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Failed to verify email");
    }
  };

  const handleBulkVerifyEmails = async () => {
    const pattern = window.prompt(
      "Enter email pattern to verify (e.g., 'member' or '@recoverymatch.com'):\n\nThis will verify all matching users' emails."
    );
    if (!pattern) return;
    
    try {
      const response = await api.post(`/admin/users/verify-emails?email_pattern=${encodeURIComponent(pattern)}`);
      toast.success(response.data.message);
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Failed to bulk verify emails: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This cannot be undone.")) return;
    
    try {
      await api.deleteAdminUser(userId);
      toast.success("User deleted");
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleUpdateTier = async (userId, tier) => {
    try {
      await api.updateAdminUser(userId, { subscription_tier: tier });
      toast.success(`Updated to ${tier}`);
      fetchUsers(page, search);
      setSelectedUser(null);
    } catch (error) {
      toast.error("Failed to update tier");
    }
  };

  const handleGrantLifetime = async (userId, userEmail) => {
    const confirmed = window.confirm(`Grant LIFETIME Recovery Elite to ${userEmail}?\n\nThis will give them permanent access to all premium features.`);
    if (!confirmed) return;
    
    try {
      await api.updateAdminUser(userId, { 
        subscription_tier: "recovery_elite",
        is_lifetime_subscription: true,
        subscription_expires: "2099-12-31T23:59:59Z"
      });
      toast.success(`🎉 Granted Lifetime Elite to ${userEmail}!`);
      fetchUsers(page, search);
      setSelectedUser(null);
    } catch (error) {
      toast.error("Failed to grant lifetime subscription");
    }
  };

  const handleUpdateGender = async (userId, gender) => {
    try {
      await api.updateAdminUser(userId, { gender });
      toast.success(`Gender updated to ${gender}`);
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Failed to update gender");
    }
  };

  const handleUpdateSeekingGender = async (userId, seekingGender) => {
    try {
      await api.updateAdminUser(userId, { seeking_gender: seekingGender });
      toast.success(`Looking for updated to ${seekingGender.join(', ')}`);
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Failed to update seeking gender");
    }
  };

  const handleGrantCoins = async (userId, userEmail) => {
    const amount = window.prompt("Enter number of coins to grant:", "100");
    if (!amount) return;
    
    const coins = parseInt(amount);
    if (isNaN(coins) || coins <= 0) {
      toast.error("Please enter a valid positive number");
      return;
    }
    
    const reason = window.prompt("Reason for granting coins:", "Admin compensation");
    if (!reason) return;
    
    try {
      await api.adjustUserCoins(userId, coins, reason);
      toast.success(`Granted ${coins} coins to ${userEmail}`);
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Failed to grant coins");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error("Select users first");
      return;
    }
    
    const confirmMsg = action === 'delete' 
      ? `Delete ${selectedUsers.length} users? This cannot be undone.`
      : `${action} ${selectedUsers.length} users?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await api.bulkUserAction(selectedUsers, action);
      toast.success(`${action} completed for ${selectedUsers.length} users`);
      setSelectedUsers([]);
      fetchUsers(page, search);
    } catch (error) {
      toast.error("Bulk action failed");
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const tierColors = {
    free: "bg-gray-100 text-gray-700",
    basic: "bg-gray-100 text-gray-700",
    recovery_plus: "bg-[#FAB710]/20 text-[#B8860B]",
    gold: "bg-[#FAB710]/20 text-[#B8860B]",
    recovery_elite: "bg-[#1C3A7A]/10 text-[#1C3A7A]",
    platinum: "bg-[#1C3A7A]/10 text-[#1C3A7A]"
  };

  const tierLabels = {
    free: "Free",
    basic: "Free",
    recovery_plus: "Recovery+",
    gold: "Recovery+",
    recovery_elite: "Recovery Elite",
    recovery_elite: "Recovery Elite"
  };

  const tierOptions = [
    { value: "free", label: "Free", color: "bg-gray-100 text-gray-700", description: "3 daily matches" },
    { value: "recovery_plus", label: "Recovery+", color: "bg-[#FAB710]/20 text-[#B8860B]", description: "10 matches, unlimited messaging" },
    { value: "recovery_elite", label: "Recovery Elite", color: "bg-[#1C3A7A]/10 text-[#1C3A7A]", description: "15 matches, video calls, advanced filters" }
  ];

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch {
      return "Unknown";
    }
  };

  const formatLocation = (location) => {
    if (!location) return null;
    if (typeof location === 'string') return location;
    if (typeof location === 'object') {
      const parts = [location.city, location.state].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : null;
    }
    return null;
  };

  const getFilteredUsers = () => {
    let filtered = users;
    if (filterStatus === "suspended") {
      filtered = users.filter(u => u.is_suspended);
    } else if (filterStatus === "verified") {
      filtered = users.filter(u => u.is_verified);
    } else if (filterStatus === "premium") {
      filtered = users.filter(u => u.subscription_tier && u.subscription_tier !== "basic");
    }
    return filtered;
  };

  return (
    <AdminLayout>
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-16 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-[#1C3A7A]">User Management</h1>
              <p className="text-sm text-gray-500">{totalUsers.toLocaleString()} total users</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleBulkVerifyEmails}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Bulk Verify Emails
              </Button>
              <Button
                onClick={() => fetchUsers(page, search)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 h-10 bg-gray-100 border-none"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              { label: "All", value: "all" },
              { label: "Suspended", value: "suspended" },
              { label: "Verified", value: "verified" },
              { label: "Premium", value: "premium" }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => {
                  setFilterStatus(filter.value);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filterStatus === filter.value
                    ? 'bg-[#1C3A7A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <div className="bg-[#1C3A7A] px-4 py-3 sticky top-[140px] z-10">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <span className="text-white text-sm">{selectedUsers.length} users selected</span>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => handleBulkAction('verify')}
              >
                <UserCheck className="w-4 h-4 mr-1" /> Verify
              </Button>
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => handleBulkAction('suspend')}
              >
                <Ban className="w-4 h-4 mr-1" /> Suspend
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={() => handleBulkAction('delete')}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                className="text-white hover:text-white hover:bg-white/20"
                onClick={() => setSelectedUsers([])}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#FAB710] border-t-transparent"></div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto p-4">
          {/* Select All */}
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              checked={selectedUsers.length === users.length && users.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-600">Select all on page</span>
          </div>

          <div className="space-y-3">
            {getFilteredUsers().length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No users found on this page</p>
                {filterStatus !== "all" && (
                  <button 
                    onClick={() => setFilterStatus("all")}
                    className="text-[#1C3A7A] underline mt-2"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            ) : (
              getFilteredUsers().map((user) => (
              <div key={user.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleSelectUser(user.id)}
                      className="w-4 h-4 rounded border-gray-300 mt-3"
                    />
                    
                    {/* Avatar */}
                    <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                      {user.photos?.[0] ? (
                        <img
                          src={user.photos[0]}
                          alt={user.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <User className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{user.name}</h3>
                        {user.is_suspended && (
                          <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Ban className="w-3 h-3" /> Suspended
                          </span>
                        )}
                        {user.is_verified && (
                          <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <Mail className="w-3 h-3" />
                        <span className="truncate">{user.email}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <button
                          onClick={() => setSelectedUser(selectedUser === user.id ? null : user.id)}
                          className={`px-2 py-1 rounded-full text-xs font-medium hover:ring-2 hover:ring-offset-1 hover:ring-gray-300 transition-all ${tierColors[user.subscription_tier || 'free']} ${
                            selectedUser === user.id ? 'ring-2 ring-offset-1 ring-[#1C3A7A]' : ''
                          }`}
                          title="Click to manage subscription"
                        >
                          <Crown className="w-3 h-3 inline mr-1" />
                          {tierLabels[user.subscription_tier || 'free']}
                        </button>
                        {user.age && (
                          <span className="text-xs text-gray-500">{user.age} years old</span>
                        )}
                        {user.location && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {formatLocation(user.location) || 'Unknown'}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                        className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                        title="More details"
                      >
                        {expandedUser === user.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleSuspend(user.id, user.is_suspended)}
                        className={`p-2 rounded-lg ${
                          user.is_suspended
                            ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                        }`}
                        title={user.is_suspended ? 'Unsuspend' : 'Suspend'}
                      >
                        {user.is_suspended ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Subscription tier selector */}
                  {selectedUser === user.id && (
                    <div className="mt-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-700">Manage Subscription</p>
                        <button 
                          onClick={() => setSelectedUser(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Current Status */}
                      <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Current Tier</p>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${tierColors[user.subscription_tier || 'free']}`}>
                              <Crown className="w-3 h-3 inline mr-1" />
                              {tierLabels[user.subscription_tier || 'free']}
                            </span>
                          </div>
                          {user.subscription_expires && (
                            <div className="text-right">
                              <p className="text-xs text-gray-500 mb-1">Expires</p>
                              <p className="text-sm font-medium text-gray-700">
                                {formatDate(user.subscription_expires)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tier Options */}
                      <p className="text-xs text-gray-500 mb-2">Change to:</p>
                      <div className="space-y-2">
                        {tierOptions.map(tier => (
                          <button
                            key={tier.value}
                            onClick={() => handleUpdateTier(user.id, tier.value)}
                            disabled={(user.subscription_tier || 'free') === tier.value}
                            className={`w-full p-3 rounded-lg text-left transition-all ${
                              (user.subscription_tier || 'free') === tier.value 
                                ? 'bg-gray-100 opacity-50 cursor-not-allowed border-2 border-gray-300' 
                                : 'bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${tier.color}`}>
                                  {tier.label}
                                </span>
                                <span className="text-xs text-gray-500">{tier.description}</span>
                              </div>
                              {(user.subscription_tier || 'free') === tier.value && (
                                <Check className="w-4 h-4 text-green-500" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Expanded Details */}
                {expandedUser === user.id && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">User ID</p>
                        <p className="text-sm font-mono text-gray-700 truncate">{user.id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Joined</p>
                        <p className="text-sm text-gray-700">{formatDate(user.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Gender</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-700">{user.gender || 'Not set'}</p>
                          <select 
                            className="text-xs border rounded px-1 py-0.5"
                            value={user.gender || ''}
                            onChange={(e) => handleUpdateGender(user.id, e.target.value)}
                          >
                            <option value="">Set gender</option>
                            <option value="Man">Man</option>
                            <option value="Woman">Woman</option>
                            <option value="Transgender Man">Transgender Man</option>
                            <option value="Transgender Woman">Transgender Woman</option>
                            <option value="Non-binary">Non-binary</option>
                            <option value="Genderqueer">Genderqueer</option>
                            <option value="Genderfluid">Genderfluid</option>
                            <option value="Agender">Agender</option>
                            <option value="Two-Spirit">Two-Spirit</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Looking for</p>
                        <div className="flex flex-col gap-1">
                          <p className="text-sm text-gray-700">{user.seeking_gender?.length > 0 ? user.seeking_gender.join(', ') : 'Not set'}</p>
                          <div className="flex gap-1 flex-wrap">
                            {['Men', 'Women', 'Trans Men', 'Trans Women', 'Non-binary people', 'Everyone'].map(g => (
                              <button
                                key={g}
                                className={`text-xs px-2 py-0.5 rounded border ${user.seeking_gender?.includes(g) ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}
                                onClick={() => {
                                  const current = user.seeking_gender || [];
                                  const updated = current.includes(g) 
                                    ? current.filter(x => x !== g) 
                                    : [...current, g];
                                  handleUpdateSeekingGender(user.id, updated);
                                }}
                              >
                                {g}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {user.bio && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-1">Bio</p>
                        <p className="text-sm text-gray-700">{user.bio}</p>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      {!user.is_verified && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleVerify(user.id)}
                          className="flex items-center gap-1"
                        >
                          <Shield className="w-4 h-4" /> Verify User
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGrantCoins(user.id, user.email)}
                        className="flex items-center gap-1 text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                      >
                        <Coins className="w-4 h-4" /> Grant Coins
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGrantLifetime(user.id, user.email)}
                        className="flex items-center gap-1 text-purple-600 border-purple-300 hover:bg-purple-50"
                      >
                        <Crown className="w-4 h-4" /> Grant Lifetime Elite
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(user.id)}
                        className="flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" /> Delete User
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
            )}
          </div>
          
          {/* Pagination */}
          {totalPages >= 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Showing {((page - 1) * 20) + 1} - {Math.min(page * 20, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  First
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <div className="flex flex-wrap items-center justify-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        type="button"
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          console.log(`[Pagination] Clicking page ${pageNum}, current: ${page}`);
                          setPage(pageNum);
                        }}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    console.log(`[Pagination] Clicking Next, current: ${page}, going to: ${page + 1}`);
                    setPage(prev => Math.min(totalPages, prev + 1));
                  }}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    console.log(`[Pagination] Clicking Last, going to: ${totalPages}`);
                    setPage(totalPages);
                  }}
                  disabled={page === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminUsers;
